// Scoped realtime subscription for meet. Different pages need different
// table+filter combinations; passing { tables, roomId } keeps each
// subscription tight so the home page doesn't receive every utterance from
// every active call across the org.
//
// Module-level subscription handle (NOT root state) — DOMQL may create a
// fresh state object on update, so direct property assignments
// (root._meetRealtimeChannel) get lost on the next update and every
// callback fires against the dead state object. Module-level vars stay
// stable across state updates.
//
// All event callbacks re-read `this.state.root` freshly via the captured
// `self` element reference so updates always land on the live root state,
// not a stale snapshot.
//
// Named distinctly from chat's subscribeRealtime to avoid function-registry
// collision. Declared as a regular `function` (not arrow) so this-binding
// from el.call(...) works.
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.meet`
// dispatches through the SDK's transport-agnostic provider with a
// per-page `tables` selector. The provider tags each callback by table
// kind ('room'|'participant'|'waiting'|'transcript'|'analysis') so the
// dispatch table below routes events without re-reading the table name.

import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { getActiveWorkspacePointer } from '@symbo-ls/workspace-shared/functions/activeWorkspacePointer.js'
import { isLoudDevRuntime } from '@symbo-ls/workspace-shared/functions/isLoudDevRuntime.js'
import { rowToRoom, rowToParticipant, rowToUtterance, dedupeUtterances } from './meetUtils.js'

// Active workspace this tab is viewing — the RECEIVER's own resolved
// workspace (root.workspace.id — URL-derived, per-tab: each browser tab has
// its own JS context/state object, so this can never be poisoned by another
// tab) FIRST, the shared localStorage pointer only as a cold-boot fallback
// before root.workspace has resolved.
//
// FIXED 2026-08-08 (tickets/workspace.md P1, "defence in depth" pass): this
// used to read the pointer FIRST and root.workspace.id only as a fallback —
// backwards, and it made the dispatch-time guard below a no-op in exactly
// the scenario that ticket describes. With MWT off (the default),
// `localStorage.activeWorkspace` is shared across every tab; if it read
// wrong here, it was ALSO what this same subscription resolved its scope
// from (see `wsId` below and _open's caller) — so the guard was comparing a
// poisoned value against itself and always "agreeing". root.workspace.id
// can't be poisoned that way, so it must win whenever it's resolved.
//
// Null in the unauthenticated guest flow (guest.js), where scoping is a
// no-op by design — the invite token scopes the room.
// Exported (alongside _makeDispatch below) so
// subscribeMeetRealtime.test.js can cover the workspace-isolation guard
// directly, without mocking the SDK/getService promise chain that _open
// wires — the guard logic itself doesn't touch the SDK at all.
export const _meetActiveWsId = (self) => {
  const rootWs = self?.state?.root?.workspace?.id
  if (rootWs) return rootWs
  try {
    return getActiveWorkspacePointer() || null
  } catch (_) {
    return null
  }
}

// TWO independent subscription slots (2026-07-29 per Nika "really fix
// transcripts not showing up in realtime — nor on video and also in
// sidebar"):
//
//   PAGE slot — whatever meet surface is on screen (list view broad scope,
//   transcript page narrowed to its room). Pages replace each other's
//   scope freely, as before.
//   CALL slot — pinned to the CONNECTED room for the whole life of the
//   call, owned by connectCall/disconnectCall, NOT by any page.
//
// They used to be ONE module-level slot, so every page re-scope REPLACED
// the in-call subscription: visiting the room list mid-call (broad scope,
// no transcripts) or opening another room's transcript silently killed the
// live captions + transcript panel for the active call — and the
// persistent-call dock (navigation never disconnects, 2026-07-29) made
// mid-call navigation the NORMAL case, so realtime "randomly" died.
let _pageUnsub = null
let _pageScope = null
let _callUnsub = null
let _callScope = null

const _rowToWaitingGuest = (r) => ({
  id: r.id,
  roomId: r.room_id,
  guestName: r.guest_name || 'meet.waiting.guest',
  guestEmail: r.guest_email || null,
  requestedAt: r.requested_at,
  admittedAt: r.admitted_at,
  rejectedAt: r.rejected_at
})

// Apply INSERT/UPDATE/DELETE for an id+roomId-keyed map state slot
// (liveParticipants, waitingRoom). Skips RLS-masked payloads (missing
// id or roomId) — the next page-level fetch reconciles.
const _applyKeyedByRoom = (root, stateKey, payload, item) => {
  if (!item.id || !item.roomId) return
  const map = { ...(root[stateKey] || {}) }
  const list = map[item.roomId] || []
  if (payload.eventType === 'INSERT') {
    if (!list.some((x) => x.id === item.id)) map[item.roomId] = [...list, item]
  } else if (payload.eventType === 'UPDATE') {
    map[item.roomId] = list.map((x) => (x.id === item.id ? item : x))
  } else if (payload.eventType === 'DELETE') {
    map[item.roomId] = list.filter((x) => x.id !== item.id)
  }
  root.update({ [stateKey]: map })
}

// Reconcile room/transcript/analysis state from a `meet.snapshot` (fires on
// EVERY connect AND every reconnect — MeetStreamController.stream writes it
// unconditionally before wiring the live subscribe, so a fresh EventSource
// always gets a fresh one; verified server-side, not assumed). Before the
// SDK forwarded this frame (tickets/opus.md "`meet` realtime discards the
// server's meet.snapshot, so a reconnect recovers nothing"), a reconnect
// recovered NOTHING: any room/utterance/analysis write that landed
// server-side during the gap — with no matching `meet.room.*` /
// `meet.transcript.insert` / `meet.analysis.*` event ever delivered for it,
// by definition of "the connection was down" — was silently lost. This is
// the exact receiver-side reconcile branch `chat`'s `_handleSnapshot`
// (workspace/packages/chat/functions/subscribeRealtime.js) already has for
// `chat.snapshot`; this mirrors it.
//
// Server shape (MeetStreamController._buildSnapshot) — a key's PRESENCE is
// itself the signal, not its truthiness: a key is included only for the
// kinds THIS subscription requested (see server's `want(kind)`), so an
// ABSENT key means "not requested this scope" and must be left untouched,
// while a PRESENT-but-empty array means "requested, and the answer is none"
// (real state — see the `analysis` branch below):
//   room view  (roomId scope):      { rooms?: [row]|[], transcripts?: [row…], analysis?: [row]|[] }
//   list view  (workspaceId scope): { rooms?: [row…] }   (never transcripts/analysis)
// `waiting` is deliberately never included — it's host-only and not exposed
// via a service-role single-room read; the live `meet.waiting.*` events
// (already handled below) carry the queue, same as the server comment says.
//
// rooms/transcripts use an UPSERT-by-id merge (byId Map, same pattern
// chat's own snapshot handler uses for channels) — never a replace. This
// function is shared by BOTH subscription slots (see the module doc above):
// the CALL slot's snapshot is room-scoped and carries exactly ONE room, so
// replacing root.rooms outright would wipe every other room the broad PAGE
// slot/list-view fetch already loaded. `analysis` is a single derived row
// (not a keyed collection, matching root.analysis's existing contract — see
// TranscriptPage.js's own `transform: (rows) => ({ analysis: list[0] ||
// null } )` REST-refetch, which already overwrites outright), so it's
// written directly, not merged.
//
// Deliberately NO per-row workspace_id filter here — same reasoning
// `_makeDispatch`'s `room` branch already documents for the cross-workspace
// townhall carve-out: the ONLY scope guard that applies is the
// wsId-staleness check already run before this is called (see
// `_makeDispatch` below), never a raw row-level equality check.
const _handleMeetSnapshot = (root, snap) => {
  if (!snap || typeof snap !== 'object') return

  if (Array.isArray(snap.rooms) && snap.rooms.length) {
    const cur = root.rooms || []
    const byId = new Map(cur.map((r) => [r.id, r]))
    for (const raw of snap.rooms) {
      const room = rowToRoom(raw || {})
      if (!room.id) continue
      byId.set(room.id, room)
    }
    root.update({ rooms: [...byId.values()] })
  }

  if (Array.isArray(snap.transcripts) && snap.transcripts.length) {
    const mapped = snap.transcripts.map((raw) => rowToUtterance(raw || {})).filter((u) => u.id)
    const cur = root.utterances || []
    root.update({ utterances: dedupeUtterances([...cur, ...mapped]) })
  }

  // Presence (`Array.isArray`), not truthiness — an empty array still means
  // this scope DID request analysis and the answer is none, matching
  // root.analysis's established null-means-resolved contract (pages/index.js
  // gates auto-analyze on `root.analysis === undefined` meaning "still in
  // flight"; `null` is a real, settled answer).
  if (Array.isArray(snap.analysis)) {
    root.update({ analysis: snap.analysis[0] || null })
  }
}

// Map page-level table names to provider-level table kinds.
const _tablesToKinds = (tables) => {
  const map = {
    meet_rooms: 'rooms',
    meet_participants: 'participants',
    meet_waiting_room: 'waiting',
    meet_transcripts: 'transcripts',
    meet_transcript_analyses: 'analyses'
  }
  return tables.map((t) => map[t]).filter(Boolean)
}

// Shared (kind, payload) dispatch — both slots write the SAME state slots,
// so an event arriving on either produces identical updates (the appenders
// are id-deduped, so page+call overlap for the same room double-delivers
// harmlessly).
//
// `wsId` is the workspace the subscription was OPENED for (PAGE slot only —
// the CALL slot never passes one, see subscribeMeetCallRealtime below).
// Events landing after a workspace switch (the window where the old channel
// is still connected because nobody re-subscribed yet) are dropped instead
// of writing the old workspace's rooms/participants/utterances into the new
// workspace's state. This is the RECEIVER-side defense-in-depth guard
// (tickets/workspace.md P1, 2026-08-08, resolution option 2) — it now
// compares against `_meetActiveWsId(self)`'s FIXED priority (root.workspace.id
// before the shared pointer), so it verifies the tab's own truth instead of
// a value that could already be shared/poisoned across tabs.
//
// Deliberately NOT a row-level `payload.new/old.workspace_id` compare (every
// meet row IS wire-stamped with `workspace_id` — server/src/domains/meet/
// store/mongoStore.js — so that data exists). MeetStreamService._scopeMatches
// documents why on purpose: "a user can be a member of a cross-workspace
// townhall room whose workspace_id differs from their active workspace, and
// that room must still deliver" (GAP-6 — the per-room ACL, not workspace
// equality, is the authoritative gate). A raw row.workspace_id !== cur check
// here would silently break that shipped feature. The staleness check below
// is the correct layer for meet: "is this dispatch still fresh for the scope
// THIS subscription was opened under", not "does this room's own workspace
// label match the viewer's active workspace" — those are different
// questions, and only the first one is this receiver's to answer.
export const _makeDispatch = (self, wsId) =>
  function (kind, payload) {
    if (wsId) {
      const cur = _meetActiveWsId(self)
      if (cur && cur !== wsId) {
        if (isLoudDevRuntime()) {
          console.warn(
            `[meet realtime] dropped ${kind} event — subscription was opened for workspace ${wsId}, tab is now on ${cur}`
          )
        }
        return
      }
    }
    const root = self.state.root

    if (kind === 'meet.snapshot') {
      _handleMeetSnapshot(root, payload)
      return
    }

    if (kind === 'room') {
      const next = rowToRoom(payload.new || payload.old || {})
      if (!next.id) return // RLS-masked payload
      const cur = root.rooms || []
      if (payload.eventType === 'INSERT') {
        if (!cur.some((r) => r.id === next.id)) root.update({ rooms: [next, ...cur] })
      } else if (payload.eventType === 'UPDATE') {
        root.update({ rooms: cur.map((r) => (r.id === next.id ? next : r)) })
      } else if (payload.eventType === 'DELETE') {
        root.update({ rooms: cur.filter((r) => r.id !== next.id) })
      }
      return
    }

    if (kind === 'participant') {
      _applyKeyedByRoom(
        root,
        'liveParticipants',
        payload,
        rowToParticipant(payload.new || payload.old || {})
      )
      return
    }

    if (kind === 'transcript') {
      const u = rowToUtterance(payload.new || {})
      if (!u.id) return // RLS-masked payload
      // Deduped append — rooms/participants always had id guards here;
      // transcripts did NOT, so an SSE reconnect replay or a second
      // transcriber worker duplicated the panel + captions (2026-07-29
      // per Nika). dedupeUtterances also keeps chronological order, so
      // out-of-order delivery can't shuffle the transcript.
      const cur = root.utterances || []
      root.update({ utterances: dedupeUtterances([...cur, u]) })
      // A spoken ask of the local participant ("Simone, …") — functions/meetFly.js.
      try {
        if (self && typeof self.call === 'function') self.call('meetFlyHeard', u)
      } catch (err) {
        console.warn('[meet] fly ask not heard:', err?.message || err)
      }
      // The call's summary builds as the transcript grows (functions/meetLiveRecap.js).
      try {
        if (self && typeof self.call === 'function') self.call('meetLiveRecap', u)
      } catch (err) {
        console.warn('[meet] live recap not counted:', err?.message || err)
      }
      return
    }

    if (kind === 'analysis') {
      if (payload.new) root.update({ analysis: payload.new })
      return
    }

    if (kind === 'waiting') {
      _applyKeyedByRoom(
        root,
        'waitingRoom',
        payload,
        _rowToWaitingGuest(payload.new || payload.old || {})
      )
    }
  }

// Open one SDK meet subscription (deferred-unsub pattern — the SDK may not
// be ready the instant a subscription is requested).
//
// THE SCOPE ON THE WIRE (G-3, 2026-10-28). A room view is scoped by its room
// alone (a cross-workspace room must still stream). A list view names the
// workspace it was opened for: the SDK puts `workspaceId` on the stream URL
// and binds the stream ticket to it only when the caller passes one, and the
// server's fallback for a missing one is the account's STORED active
// organisation — null on a white-label host (the shell resolves the host's
// workspace in memory and never writes that pointer: 400 scope_required,
// retried by the SDK forever), and an ORGANIZATION id elsewhere, while a room
// carries its WORKSPACE id (the list snapshot matched no current room).
const _open = (self, { roomId, kinds, wsId }) => {
  let _resolved = null
  let _cancelled = false
  const dispatch = _makeDispatch(self, wsId)
  const scope = roomId ? { roomId, tables: kinds } : { workspaceId: wsId, tables: kinds }

  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeMeet) return
      _resolved = wp.realtime.subscribeMeet(scope, dispatch)
    })
    .catch((err) => {
      console.warn('[meet] subscribeMeetRealtime failed:', err?.message || err)
    })

  return () => {
    _cancelled = true
    if (typeof _resolved === 'function') {
      try {
        _resolved()
      } catch (_) {}
    }
  }
}

// PAGE slot — semantics unchanged for every existing page caller: same
// signature, same scope-key idempotence, pages replace each other freely.
// The scope key carries the ACTIVE WORKSPACE id, so a re-invocation with
// identical args after a workspace switch is NOT a no-op: it tears down
// the old workspace's channel and opens a fresh one under the new claim.
export const subscribeMeetRealtime = function (opts = {}) {
  const self = this // stable element reference
  const { tables = ['meet_rooms'], roomId = null } = opts

  const wsId = _meetActiveWsId(self)
  const scopeKey = `${wsId || '*'}:${[...tables].sort().join(',')}:${roomId || '*'}`
  if (_pageScope === scopeKey) return _pageUnsub
  if (_pageUnsub) {
    try {
      _pageUnsub()
    } catch (_) {}
    _pageUnsub = null
  }
  _pageScope = scopeKey

  const kinds = _tablesToKinds(tables)
  if (!kinds.length) return null
  // A list view with no workspace resolved yet has nothing to scope it to
  // (see _open): open nothing and keep no scope, so the next call once the
  // workspace lands opens it — the shell's route-sync self-heal, the meet
  // list page's onUpdate and a workspace switch all call again.
  if (!roomId && !wsId) {
    _pageScope = null
    return null
  }

  const unsub = _open(self, { roomId, kinds, wsId })
  _pageUnsub = () => {
    unsub()
    _pageScope = null
  }
  return _pageUnsub
}

// CALL slot — pinned to the CONNECTED room, owned by connectCall /
// disconnectCall, survives every page navigation and page-slot re-scope.
// Carries the full kind set for the room so captions, the transcript
// panel, the waiting banner and the analysis card all stay live wherever
// the user roams while connected.
//
// Deliberately NOT workspace-guarded (no wsId passed to _open): the slot
// is already scoped to ONE explicit roomId, and the persistent-call dock
// contract (2026-07-29) keeps captions live even while the user browses
// another workspace mid-call — a workspace guard here would kill them.
export const subscribeMeetCallRealtime = function (roomId) {
  const self = this
  if (!roomId) return null
  if (_callScope === String(roomId)) return _callUnsub
  if (_callUnsub) {
    try {
      _callUnsub()
    } catch (_) {}
    _callUnsub = null
  }
  _callScope = String(roomId)
  const unsub = _open(self, {
    roomId,
    kinds: ['rooms', 'participants', 'waiting', 'transcripts', 'analyses']
  })
  _callUnsub = () => {
    unsub()
    _callScope = null
  }
  return _callUnsub
}

export const unsubscribeMeetCallRealtime = function () {
  if (_callUnsub) {
    try {
      _callUnsub()
    } catch (_) {}
    _callUnsub = null
  }
  _callScope = null
}
